import { createContext, useContext, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import { areEqual } from 'Utils/equalityChecks';
import { useDispatch, useSelector } from 'react-redux';

// types
import { UserModel } from 'Containers/User/Models/UserModel';

import { userSelector } from 'Containers/User/selector';
import { userDetails } from 'Containers/User';
import { isCompanyAdmin } from 'Utils/roles';
import { setAppInitialLoading, setAppRedirectPathLocal } from 'Containers/Core/actions';
import { shouldGetAuth } from 'Utils/helpers';
import { pusherSelector } from 'Containers/Core/selectors';

// initializing the user context variables, functions
export const UserContext = createContext(<UserModel>{});

// set user context variables, functions
export const userProviderFunctions = () => {
  const dispatch = useDispatch();

  const pusher = useSelector(pusherSelector, areEqual);

  const userData: UserModel = useSelector(userSelector, areEqual);
  const [user, setUser] = useState(<UserModel>{});

  useEffect(() => {
    if (userData?.id) {
      // get user data
      const { roles, id, email, full_name: fullName, first_name: firstName, last_name: lastName } = userData;

      // set user for the app usage
      setUser({
        ...userData,
        isCompanyAdmin: isCompanyAdmin(roles),
        isGhlUser: !!userData.is_ghl_user,
      });

      Sentry.setUser({
        id: String(id),
        email,
        username: fullName || [firstName, lastName].filter(Boolean).join(' '),
      });

      // NOTE: feature flags are no longer fetched here. userDetails() dispatches userFeatureFlags()
      // itself, gated on the active-company sync, so flags are never read for the wrong company.
      return;
    }

    setUser(<UserModel>{});
    Sentry.setUser(null);
  }, [dispatch, userData]);

  useEffect(() => {
    const {
      location: { pathname },
    } = window;

    if (pathname.length > 1 && !shouldGetAuth(pathname)) {
      setAppRedirectPathLocal(pathname);
    } else {
      setAppRedirectPathLocal('');
    }

    // only fetch user details on private pages
    if (!shouldGetAuth(pathname)) {
      dispatch(userDetails(true));
    } else {
      dispatch(setAppInitialLoading(false));
    }
  }, []);

  // refresh user on role changed
  useEffect(() => {
    if (userData?.id) {
      // user role changed event
      pusher
        ?.subscribe(`BroadcastUserRoleChangedEvent.User.${userData?.id}`)
        ?.bind('App\\Events\\BroadcastUserRoleChangedEvent', () => {
          dispatch(userDetails(true));
        });
    }

    return () => {
      pusher?.unsubscribe(`BroadcastUserRoleChangedEvent.User.${userData?.id}`);
    };
  }, [pusher, userData]);

  return Object.freeze(user);
};

// export the userContext so we can use user properties anywhere in the application
export const useUser = () => useContext(UserContext);
